/*
 * 작성자 : 홍선영
 * 날짜 : 2023.10.10
 * 기능 : 사용자 정보 모달
 */

import { useEffect, useState, useRef, Dispatch, SetStateAction } from 'react';
import { toast } from 'react-toastify';
import { IoPersonAdd } from 'react-icons/io5';
import { useRecoilValue, useResetRecoilState, useRecoilState } from 'recoil';
import styled from 'styled-components';

import { INumbers, ISelectBox } from 'customTypes';
import { IUser, userState, themeState } from '../../atoms';
import { ModalBackground, Modal } from '../../assets/styles/Modal';
import { isMobile } from '../../utils/checkMobile';
import Language from '../language';
import { applyBorderStyle } from '../../utils/applyBorderStyle';
import { phoneNumRegex, emailRegex, passwordOnlyRegex, passwordRegex, telNumRegex } from '../../utils/checkRegex';
import Input from '../Input';
import IpInput from '../IpInput';
import NumbersInput from '../NumbersInput';
import { BtnBlue, BtnGray, BtnGhost } from '../Button';
import { logPost } from '../../services/log';
import { registerIp, registerPwd } from '../../services/register';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import { apiDelete, apiGet, apiPatch } from '../../services/_common';
import { PASSWORD_TYPE_SHORT_VERSION, PMS_NORMAL_SUB_CODE } from '../../_constants';
import SelectBox from '../SelectBox';
import DatePickerComponent from '../DatePicker';
import { AddressSearch } from '../AddressSearch';
import ClickableContainer from '../../pages/h_cctv/RealtimeCCTV/ClickableContainer';
import TableLamp from '../TableLamp';
import { getAnnualnum } from '../../utils/getAnnualnum';
import GetFetchStatus from '../../hooks/GetFetchStatus';
import { PulseLoader } from 'react-spinners';
import { processFile } from '../../hooks/crossPlatformHook/useFlutterFilePicker';
import { handleFlutterLogoutAndRemoveCookies, setupFlutterFileHandlers } from '../../utils/flutterBridge';
import IssueGuide from '../IssueGuide';
import useBackHistory from '../../hooks/useBackHistory';

interface IPasswordState {
  password: string;
  passwordConfirm: string;
}

// Compression configuration
const config = {
  quality: 0.7,
  maxWidth: 800,
  maxHeight: 600,
  autoRotate: true,
  debug: true,
};

const ModalHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
  margin-right: 0.5rem;

  .icon-btn {
    padding: 0 0.25rem;
    width: 2.5rem;
    height: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.25rem;
    cursor: pointer;
    background-color: transparent;
    border: none;
    color: var(--text-tertiary);
    transition: all 0.2s ease;

    &:hover {
      background-color: var(--tonal-deep);
      color: var(--text-primary);
    }

    .material-symbols-rounded {
      font-size: 1.5rem;
    }
  }

  .language-select-wrapper {
    width: 2.5rem;
    height: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.2s ease;
    padding: 0;

    &:hover {
      background-color: var(--tonal-deep);
    }

    > div {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    /* react-select 커스터마이징: 높이/배경 정렬 */
    .select__control {
      min-height: 100%;
      height: 100%;
      width: 100%;
      border: none;
      box-shadow: none;
      background: transparent;
      cursor: pointer;
    }
    .select__control--is-focused {
      box-shadow: none;
      border: none;
    }
    .select__value-container {
      padding: 0;
    }
    .select__single-value {
      margin-left: 0;
      margin-right: 0;
    }
    .select__indicators,
    .select__indicator-separator {
      display: none;
    }
    .select__menu {
      left: 50%;
      transform: translateX(-50%);
      min-width: 2.5rem;
      width: 2.5rem;
      text-align: center;
    }
  }
`;

const UserInfoSettingModal = ({ openModal, setOpenModal, onClickClose, closeAside }: any) => {
  const { t } = useTranslation();
  const accessToken = Cookies.get('prosafeToken');
  const userInfo = useRecoilValue<IUser>(userState);
  const resetUserInfo = useResetRecoilState(userState);
  const [theme, setTheme] = useRecoilState(themeState);
  const focusRef = useRef<HTMLInputElement>(null);
  const userNameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const [signupInfo, setSignupInfo] = useState({
    userId: '',
    userName: '',
    hpNum: '',
    telNum: '',
    eMail: '',
    ipInfo: '',
    // userPwd: '',
    partCd: '',
    positionCd: '',
    uImg: '',
    emerNum: '',
    uBdate: '',
    uBgubun: '0',
    uAnnualnum: '',
    uRannualnum: '',
    zipcode: '',
    postNum: '',
    address1: '',
    address2: '',
    latitude: '',
    longitude: '',
    loginerrorCount: 0,
  });
  const [hpNumState, sethpNumState] = useState<INumbers>({ num1: '010', num2: '', num3: '' });
  const [telNumState, setTelNumState] = useState<INumbers>({ num1: '', num2: '', num3: '' });
  const [emerNumState, setEmerNumState] = useState<INumbers>({ num1: '', num2: '', num3: '' });
  const [ipInfoState, setIpInfoState] = useState<INumbers>({ num1: '', num2: '', num3: '', num4: '' });
  const [isSaveClicked, setIsSaveClicked] = useState(false); // 저장버튼 클릭 여부에 따라 필수입력값 보더색상 처리하기 위한 state 값
  const [pwAlert, setPwAlert] = useState(false);
  const [newPassword, setNewPassword] = useState<IPasswordState>({ password: '', passwordConfirm: '' });
  const [isSettingPassword, setIsSettingPassword] = useState(false); // 패스워드 변경여부
  const telNumLengthState = { num1: 3, num2: 4, num3: 4 }; // 전화번호 입력 길이
  const hpNumLengthState = { num1: 3, num2: 4, num3: 4 }; // 휴대전화 입력 길이
  const [telNumLastInput, setTelNumLastInput] = useState(false);
  const [hpNumLastInput, setHpNumLastInput] = useState(false);
  const [emerNumLastInput, setEmerNumLastInput] = useState(false);

  const [partList, setPartList] = useState<ISelectBox[]>([]); // 부서명 메뉴 리스트
  const [positionList, setPositionList] = useState<ISelectBox[]>([]); // 직급명 메뉴 리스트
  const [selectedPartCd, setSelectedPartCd] = useState<ISelectBox>({ type: 'PART_CD', PART_CD: '', cdName: t('미선택') });
  const [selectedPositionCd, setSelectedPositionCd] = useState<ISelectBox>({ type: 'POSITION_CD', POSITION_CD: '', cdName: t('미선택') });
  const [startDate, setStartDate] = useState('');
  const [isSolarDate, setIsSolarDate] = useState(true);
  const [userImage, setUserImage] = useState<{ isUpdate: boolean; img: string | null }>({ isUpdate: false, img: null });
  const randomString = Math.random().toString(36).substring(7); // 랜덤 문자열을 쿼리스트링으로 추가해서 이미지 캐싱 방지
  const isHeadUser = userInfo.gCd.substring(0, 1)?.toLowerCase() === 'h';
  const isCompanyUser = userInfo.gCd.substring(0, 1)?.toLowerCase() === 'c';
  const { data, isLoading, refetch } = GetFetchStatus({
    path: '/user',
    req: { userId: userInfo.userId, hCd: userInfo.hCd },
    enabledKey: userInfo.hCd.substring(0, 1).toLowerCase() !== 'm',
    hCdOnly: { value: true, key: 'hCd' },
  });

  const { onBackPrev } = useBackHistory({ isOpen: openModal.status, onPrev: onClickClose, pageName: 'userInfoSettingModal' });

  useEffect(() => {
    if (data) {
      const {
        gCd,
        hCd,
        userId,
        userName,
        // userPwd,
        eMail,
        uImg,
        zipcode: postNum,
        address1,
        address2,
        uAnnualnum,
        uRannualnum,
        pwdCheckyn,
        approvalYn,
        hpNum,
        telNum,
        emerNum,
        ipInfo,
        uBdate,
        partCd,
        partName,
        positionCd,
        positionName,
        uBgubun,
      } = data;
      setSignupInfo((prev) => ({
        ...prev,
        gCd,
        hCd,
        userId,
        userName,
        // userPwd,
        eMail,
        uImg,
        postNum,
        address1,
        address2,
        uAnnualnum: getAnnualnum(uAnnualnum),
        uRannualnum: getAnnualnum(uRannualnum),
        pwdCheckyn,
        approvalYn,
      }));

      if (hpNum) {
        const split = hpNum.split('-') ?? ['', '', ''];
        sethpNumState({ num1: split[0], num2: split[1], num3: split[2] });
      }
      if (telNum) {
        const split = telNum.split('-') ?? ['', '', ''];
        setTelNumState({ num1: split[0], num2: split[1], num3: split[2] });
      }
      if (emerNum) {
        const splitEmerNumber = emerNum?.split('-') ?? ['', '', ''];
        setEmerNumState({ num1: splitEmerNumber[0], num2: splitEmerNumber[1], num3: splitEmerNumber[2] });
      }
      if (ipInfo) {
        const split = ipInfo.split('.') ?? ['', '', '', ''];
        if (split.length === 4) setIpInfoState({ num1: split[0], num2: split[1], num3: split[2], num4: split[3] });
      }

      setUserImage({ isUpdate: false, img: uImg });
      if (uBdate) setStartDate(uBdate);
      if (partCd) setSelectedPartCd({ type: 'PART_CD', PART_CD: partCd, cdName: partName });
      if (positionCd) setSelectedPositionCd({ type: 'POSITION_CD', POSITION_CD: positionCd, cdName: positionName });
      if (uBgubun) setIsSolarDate(uBgubun === '0');
    }
  }, [data]);

  const onChangeuserName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;
    setSignupInfo((prev) => ({ ...prev, [name]: value?.trim() }));
  };

  // 패스워드 input에서 포커스 아웃되었을 때 비밀번호정규식 확인문구
  const onBlur = () => {
    const { isPasswordRegexPass } = passwordOnlyRegex(newPassword?.password);
    if (!isPasswordRegexPass) setPwAlert(true);
    else setPwAlert(false);
  };

  // 패스워드확인 input에서 포커스 아웃되었을 때 비밀번호정규식 확인문구
  const onBlurPasswordConfirm = () => {
    const { isPasswordRegexPass } = passwordOnlyRegex(newPassword?.passwordConfirm);
    if (!isPasswordRegexPass) setPwAlert(true);
    else setPwAlert(false);
  };

  // 현재IP버튼 API호출 후 ip setState
  const onClickGetCurrentIp = async () => {
    const res = await registerIp();
    const { data: resData, statusCode } = res.data;
    if (statusCode === 200) {
      const ipSplit = resData.split('.');
      setIpInfoState({ num1: ipSplit[0], num2: ipSplit[1], num3: ipSplit[2], num4: ipSplit[3] });
      setSignupInfo((prev) => ({ ...prev, ipInfo: resData }));
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 관리유저 수정 API
  const patchManagerUserInfoAPI = async (reqData: any) => {
    const { uImg, emerNum, uBdate, uBgubun, zipcode, address1, address2, partCd, positionCd, loginerrorCount, pwdCheckyn, approvalYn, ...rest } = reqData;
    let req = rest;
    if (isSettingPassword) {
      // 패스워드 변경값이 있는경우, 이미 패스워드기간 유효성 통과시 저장을 했으므로 패스워드값 제외
      /**
       * 수정자 : 한영광
       * 수정일자 : 2023.10.18
       * 수정내용 : userPwd 값이 undefined이 되는 경우 쿼리에서 비밀번호를 undefined로 저장하는 문제가 있어서 공백으로 보내도록 수정함
       */
      // const { userPwd, ...rest2 } = rest;
      const rest2 = { ...rest, userPwd: '' };
      req = rest2;
    }
    const res = await apiPatch({ path: '/user/manager', contentType: 'application/json', req });
    const { statusCode, message } = res.data;
    if (statusCode === 200) {
      setOpenModal((prev: any) => ({ ...prev, status: false }));

      if (isMobile()) closeAside && closeAside();
      toast.success(t(message));
      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '관리자 사용자정보 수정',
        action: '관리자 사용자정보 수정 성공',
        etc: ``,
      });
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 본사유저 수정 API
  const patchHeadUserInfoAPI = async (reqData: any) => {
    const { uImg, emerNum, uBdate, uBgubun, zipcode, address1, address2, pwdCheckyn, approvalYn, ...rest } = reqData;

    let req = rest;
    if (isSettingPassword) {
      // 패스워드 변경값이 있는경우, 이미 패스워드기간 유효성 통과시 저장을 했으므로 패스워드값 ''으로 전송
      const rest2 = { ...rest, userPwd: '' };
      req = rest2;
    }
    const res = await apiPatch({ path: '/user/head', contentType: 'application/json', req });
    const { statusCode, message } = res.data;
    if (statusCode === 200) {
      refetch();
      setOpenModal((prev: any) => ({ ...prev, status: false }));

      if (isMobile()) closeAside && closeAside();
      toast.success(t(message));
      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '본사 사용자정보 수정',
        action: '본사 사용자정보 수정 성공',
        etc: ``,
      });
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 현장유저 수정 API
  const patchSiteUserInfoAPI = async (reqData: any) => {
    const { uImg, emerNum, uBdate, uBgubun, zipcode, address1, address2, partCd, positionCd, ...rest } = reqData;

    let req = rest;
    if (isSettingPassword) {
      // 패스워드 변경값이 있는경우, 이미 패스워드기간 유효성 통과시 저장을 했으므로 패스워드값 ''으로 전송
      const rest2 = { ...rest, userPwd: '' };
      req = rest2;
    }
    const res = await apiPatch({ path: '/user/site', contentType: 'application/json', req });
    const { statusCode, message } = res.data;
    if (statusCode === 200) {
      setOpenModal((prev: any) => ({ ...prev, status: false }));

      if (isMobile()) closeAside && closeAside();
      toast.success(t(message));
      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '현장 사용자정보 수정',
        action: '현장 사용자정보 수정 성공',
        etc: ``,
      });
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 영업유저 수정 API
  const patchAgentUserInfoAPI = async (reqData: any) => {
    const { uImg, emerNum, uBdate, uBgubun, zipcode, address1, address2, partCd, positionCd, loginerrorCount, pwdCheckyn, approvalYn, ...rest } = reqData;
    let req = rest;
    if (isSettingPassword) {
      // 패스워드 변경값이 있는경우, 이미 패스워드기간 유효성 통과시 저장을 했으므로 패스워드값 ''으로 전송
      const rest2 = { ...rest, userPwd: '' };
      req = rest2;
    }
    const res = await apiPatch({ path: '/user/agent', contentType: 'application/json', req });
    const { statusCode, message } = res.data;
    if (statusCode === 200) {
      setOpenModal((prev: any) => ({ ...prev, status: false }));

      if (isMobile()) closeAside && closeAside();
      toast.success(t(message));
      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '영업 사용자정보 수정',
        action: '영업 사용자정보 수정 성공',
        etc: ``,
      });
    }
  };

  const patchCompanyUserInfoAPI = async (reqData: any) => {
    const { loginerrorCount, pwdCheckyn, approvalYn, ...rest } = reqData;
    let req = rest;

    if (isSettingPassword) {
      // 패스워드 변경값이 있는경우, 이미 패스워드기간 유효성 통과시 저장을 했으므로 패스워드값 ''으로 전송
      const rest2 = { ...rest, userPwd: '' };
      req = rest2;
    }
    const res = await apiPatch({ path: '/user/company', contentType: 'application/json', req });
    const { statusCode, message } = res.data;
    if (statusCode === 200) {
      refetch();
      setOpenModal((prev: any) => ({ ...prev, status: false }));
      if (isMobile()) closeAside && closeAside();
      toast.success(t(message));
      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '사내 사용자정보 수정',
        action: '사내 사용자정보 수정 성공',
        etc: ``,
      });
    }
  };

  // 회원정보수정 API 호출
  const updateUserInfoAPI = async () => {
    const mergedHpNumNumber = `${hpNumState.num1}-${hpNumState.num2}-${hpNumState.num3}`;
    const mergedEmerNumber = `${emerNumState.num1}-${emerNumState.num2}-${emerNumState.num3}`;
    const mergedTelNumber = telNumState.num1 === '' && telNumState.num2 === '' && telNumState.num3 === '' ? '' : `${telNumState.num1}-${telNumState.num2}-${telNumState.num3}`;
    const mergedIpNumber =
      ipInfoState.num1 === '' && ipInfoState.num2 === '' && ipInfoState.num3 === '' && ipInfoState.num4 === '' ? '' : `${ipInfoState.num1}.${ipInfoState.num2}.${ipInfoState.num3}.${ipInfoState.num4}`;

    const { uAnnualnum, uRannualnum, postNum, latitude, longitude, ...rest } = signupInfo;
    const reqData = {
      ...rest,
      telNum: mergedTelNumber,
      hpNum: mergedHpNumNumber,
      ipInfo: mergedIpNumber,
      userPwd: isSettingPassword ? newPassword.password : '',
      ...((isHeadUser || isCompanyUser) && { partCd: selectedPartCd.PART_CD, positionCd: selectedPositionCd.POSITION_CD }),
      ...(isCompanyUser && { uBdate: startDate, uBgubun: isSolarDate ? '0' : '1', emerNum: mergedEmerNumber, zipcode: signupInfo.postNum }),
    };
    if (userInfo.gCd.substring(0, 1) === 'M') {
      patchManagerUserInfoAPI(reqData);
    }
    if (userInfo.gCd.substring(0, 1) === 'H') {
      patchHeadUserInfoAPI(reqData);
    }
    if (userInfo.gCd.substring(0, 1) === 'S') {
      patchSiteUserInfoAPI(reqData);
    }
    if (userInfo.gCd.substring(0, 1) === 'A') {
      patchAgentUserInfoAPI(reqData);
    }
    if (userInfo.gCd.substring(0, 1) === 'C') {
      patchCompanyUserInfoAPI(reqData);
    }
  };

  // 저장버튼 클릭
  const onClickSave = async () => {
    setIsSaveClicked(true);
    const mergedHpNumNumber = `${hpNumState.num1}-${hpNumState.num2}-${hpNumState.num3}`;
    const mergedTelNumber = telNumState.num1 === '' && telNumState.num2 === '' && telNumState.num3 === '' ? '' : `${telNumState.num1}-${telNumState.num2}-${telNumState.num3}`;

    const { password, passwordConfirm } = newPassword;
    const { isPasswordRegexPass, passwordRegexAlertMsg } = passwordRegex(password, passwordConfirm);
    const { isPhoneNumRegexPass, phoneNumRegexAlertMsg } = phoneNumRegex(mergedHpNumNumber);
    const { isTelNumRegexPass, telNumRegexAlertMsg } = telNumRegex(mergedTelNumber);
    const { isEmailRegexPass, emailRegexAlertMsg } = emailRegex(signupInfo.eMail);

    if (isSettingPassword && !isPasswordRegexPass) {
      focusRef.current?.focus();
      return toast.warning(t(passwordRegexAlertMsg));
    }
    if (isSettingPassword && !(await isNewPwd())) return toast.warning(t('사용할 수 없는 비밀번호입니다.'));
    // if (isSettingPassword && signupInfo.userPwd === password) return toast.warning(t('이미 사용중인 비밀번호입니다.'));
    if (!signupInfo.userName?.trim()) {
      userNameRef.current?.focus();
      return toast.warning(t('성명을 입력하세요'));
    }
    if (!isEmailRegexPass) {
      emailRef.current?.focus();
      return toast.warning(t(emailRegexAlertMsg));
    }
    if (!isPhoneNumRegexPass) return toast.warning(t(phoneNumRegexAlertMsg));
    if (mergedTelNumber !== '--' && !isTelNumRegexPass) return toast.warning(t(telNumRegexAlertMsg));
    if (
      !(ipInfoState.num1 === '' && ipInfoState.num2 === '' && ipInfoState.num3 === '' && ipInfoState.num4 === '') &&
      (ipInfoState.num1 === '' || ipInfoState.num2 === '' || ipInfoState.num3 === '' || ipInfoState.num4 === '')
    ) {
      return toast.warning(t('접속 IP를 확인하세요'));
    }

    return updateUserInfoAPI();
  };

  // 사용자 변경 비밀번호가 비밀번호변경기간 안에 이미 사용했던 비밀번호인지 체크
  const isNewPwd = async () => {
    if (accessToken) {
      const res = await registerPwd(userInfo.userId, newPassword?.password, accessToken);
      const { statusCode, message } = res.data;
      if (statusCode !== 200) return false;
      return true;
    }
    return false;
  };

  // 비밀번호 변경버튼 클릭. 비밀번호 변경상태가 true일 때 비밀번호 값 setState
  const onClickPasswordChange = () => {
    const { password, passwordConfirm } = newPassword;

    // 비밀번호 입풋창이 열려있는 상태에서 변경버튼 클릭한 경우
    if (isSettingPassword) {
      setNewPassword({ password: '', passwordConfirm: '' });
      setIsSaveClicked(false);
    } else {
      const { isPasswordRegexPass, passwordRegexAlertMsg } = passwordRegex(password, passwordConfirm);
      if (password === '' && passwordConfirm === '') return setIsSettingPassword((prev: boolean) => !prev); // 아무것도 입력하지 않았을때 인풋닫힘
      if (!isPasswordRegexPass) return toast.warning(t(passwordRegexAlertMsg)); // 비밀번호 정규식확인
      setSignupInfo((prev: any) => ({ ...prev, password: newPassword })); // 정규식 통과시 setState
    }

    return setIsSettingPassword((prev: boolean) => !prev);
  };

  const onClickLogout = async () => {
    // Flutter WebView가 존재하는지 확인 후 로그아웃 메시지 전달
    const isFlutterWebView = handleFlutterLogoutAndRemoveCookies();
    if (isFlutterWebView) {
      await apiDelete({ path: '/user/fcmtoken' });
    }
    resetUserInfo();
    Cookies.remove('prosafeToken');
    Cookies.remove('refreshToken');
    window.location.replace('/');
  };

  // 직급명, 관리회사명 코드 조회
  const setPmsNormalSubList = async (subCd: string, setState: Dispatch<SetStateAction<ISelectBox[]>>) => {
    const req = { hCd: userInfo.hCd, sCd: userInfo.sCd, subCd };
    const res = await apiGet({ path: '/code/pmsNormalTitle', req });
    const { data: resData, status } = res;
    const cdFind: string = PMS_NORMAL_SUB_CODE.find((el) => el.code.includes(subCd))?.type || '';
    if (status === 200) {
      const newArr = resData.data.pmsNormalTitleList?.map((el: ISelectBox) => ({ type: cdFind, [cdFind]: el.subCd, cdName: el.cdName }));
      setState(newArr);
    }
  };

  useEffect(() => {
    if (isHeadUser || isCompanyUser) {
      setPmsNormalSubList('L', setPartList);
      setPmsNormalSubList('M', setPositionList);
    }
  }, []);

  const handleClickSolarDateYn = (solarYn: '0' | '1') => {
    setIsSolarDate(solarYn === '0');
  };

  const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement> | undefined) => {
    if (event) {
      const file = event.target.files && event.target.files[0];
      if (file) {
        const { isUpdate, img, imgBase64 } = await processFile(file);
        if (isUpdate) {
          setUserImage({ img: img as string, isUpdate });
          setSignupInfo((prev: any) => ({ ...prev, uImgBase64: imgBase64 }));
        }
        event.target.value = ''; // 입력 필드 초기화
      }
    }
  };

  const handleFlutterFilePicker = async (fileType: 'image' | 'all' = 'image', uploadByFlutter: boolean = false) => {
    try {
      if (window.FilePicker) {
        // Flutter 파일 선택 처리
        window.FilePicker.postMessage(JSON.stringify({ fileType, uploadByFlutter }));
      }
    } catch (error) {
      console.error('파일 처리 에러:', error);
    }
  };

  useEffect(() => {
    const cleanup = setupFlutterFileHandlers(
      (fileData) => {
        const base64 = fileData.base64 ?? '';
        setUserImage({ img: base64, isUpdate: true });
        setSignupInfo((prev: any) => ({ ...prev, uImgBase64: base64?.split(',')[1] ?? '' }));
      },
      (error) => {
        console.error('파일 선택 오류:', error);
        toast.error(t('파일 처리 중 오류가 발생했습니다.'));
      }
    );
    return cleanup;
  }, []);

  const loading = !data || isLoading || (isCompanyUser && userImage.img === null);

  const darkModeHandling = () => {
    if (theme === 'light') setTheme('dark');
    else setTheme('light');
  };

  return (
    <ModalBackground onClick={onBackPrev} role='presentation'>
      <Modal>
        <div
          className='modal userInfoSetting'
          role='presentation'
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <div className='inputForm-head'>
            <h5 className='medium'>{t(openModal?.title)}</h5>
            {onClickClose && (
              <div className='closeBtn' onClick={onBackPrev} role='presentation'>
                <span className='material-symbols-rounded'>close</span>
              </div>
            )}
          </div>
          <div className='inputForm-body user-info'>
            {loading ? (
              userInfo.hCd.substring(0, 1).toLowerCase() === 'm' ? (
                <IssueGuide text='본사 또는 회사를 선택하세요' />
              ) : (
                <div className='centered-content'>
                  <PulseLoader color='rgb(0, 122, 255)' size='10px' />
                </div>
              )
            ) : (
              <div id='join' className='modal-contents'>
                {isCompanyUser && (
                  <div className='inputForm-row'>
                    <div className='userImageWrapper fileBoxWrapper'>
                      {userImage.img !== '' ? <img src={userImage.isUpdate ? `${userImage.img}` : `${userImage.img}?v=${randomString}`} alt='user_img' /> : <IoPersonAdd size={50} />}
                      <input type='file' className='hiddenFileBox' accept='image/jpg, image/png, image/jpeg' id='filebox' name='dIfilename' onChange={handleFileInputChange} />
                      <label htmlFor='filebox' className='searchBox'>
                        <div className='smallBtn' id='filebox' role='button' tabIndex={0} onClick={() => handleFlutterFilePicker('image', false)}>
                          {t('찾아보기')}
                        </div>
                      </label>
                    </div>
                  </div>
                )}
                <div className='inputForm-row'>
                  <div className='inputForm-col col-w50'>
                    <label htmlFor='userId'>{t('아이디')}</label>
                    <input type='text' id='userId' name='userId' value={signupInfo?.userId} disabled maxLength={50} />
                  </div>
                </div>
                <div className='inputForm-row'>
                  <div className='inputForm-col col-w100'>
                    <label className='required' htmlFor='userId'>
                      {t('성명')}
                    </label>
                    <div className='flex-between iscontents-m'>
                      <input
                        ref={userNameRef}
                        type='text'
                        id='userName'
                        name='userName'
                        onChange={onChangeuserName}
                        value={signupInfo?.userName}
                        style={isSaveClicked ? applyBorderStyle(signupInfo?.userName, 'red', 'userName') : undefined}
                        maxLength={50}
                      />
                      <BtnGray className='btn-small' onClick={onClickPasswordChange}>
                        {t('비밀번호 변경')} {isSettingPassword && t('취소')}
                      </BtnGray>
                    </div>
                  </div>
                </div>
                {isSettingPassword && (
                  <>
                    <div className='inputForm-row'>
                      <div className='inputForm-col col-w50'>
                        <Input
                          name='password'
                          label={t('신규 비밀번호')}
                          type='password'
                          state={newPassword}
                          setState={setNewPassword}
                          placeholder={t('대/소문자, 숫자, 특문 중 3종류 포함')}
                          blur={onBlur}
                          maxLength={20}
                          inputRef={focusRef}
                          getBorderStyle={isSaveClicked ? applyBorderStyle(newPassword.password, 'red', 'password') : undefined}
                        />
                      </div>
                      <div className='inputForm-col col-w50'>
                        <Input
                          name='passwordConfirm'
                          label={t('신규 비밀번호 재입력')}
                          type='password'
                          state={newPassword}
                          setState={setNewPassword}
                          placeholder={t('비밀번호 확인')}
                          blur={onBlurPasswordConfirm}
                          maxLength={20}
                          getBorderStyle={isSaveClicked ? applyBorderStyle(newPassword.passwordConfirm, 'red', 'passwordConfirm') : undefined}
                        />
                      </div>
                    </div>
                    <div>
                      <small className='red text-left'>
                        {t('현재 입력 글자수')} : {newPassword.password?.length}
                      </small>
                      <small className='red text-left'>( {t(PASSWORD_TYPE_SHORT_VERSION)} )</small>
                    </div>
                  </>
                )}
                <div className='inputForm-row'>
                  <div className='inputForm-col col-w100'>
                    <div className='flex-basic'>
                      <label htmlFor='email' className='required'>
                        {t('이메일')}
                      </label>
                      <small className='text-left text_secondary'>( {t('본인 확인용 이메일이 발송됩니다. 정확히 입력해 주세요.')} )</small>
                    </div>
                    <Input
                      inputRef={emailRef}
                      className='required'
                      label=''
                      type='text'
                      id='eMail'
                      name='eMail'
                      state={signupInfo}
                      setState={setSignupInfo}
                      getBorderStyle={isSaveClicked ? applyBorderStyle(signupInfo?.eMail, 'red', 'eMail') : undefined}
                    />
                  </div>
                </div>
                <div className='inputForm-row'>
                  <div className='inputForm-col col-w100'>
                    <label htmlFor='hpNum' className='required'>
                      {t('휴대폰번호')}
                    </label>
                    <NumbersInput state={hpNumState} setState={sethpNumState} lengthState={hpNumLengthState} setIsLastInputCompleted={setHpNumLastInput} isSaveClicked={isSaveClicked} />
                  </div>
                </div>
                <div className='inputForm-row'>
                  <div className='inputForm-col col-w100'>
                    <label htmlFor='hpNum'>{t('전화번호')}</label>
                    <NumbersInput
                      state={telNumState}
                      setState={setTelNumState}
                      lengthState={telNumLengthState}
                      setIsLastInputCompleted={setTelNumLastInput}
                      isFirstInputFocused={hpNumLastInput}
                      isSaveClicked={
                        isSaveClicked &&
                        !(telNumState.num1 === '' && telNumState.num2 === '' && telNumState.num3 === '') &&
                        (telNumState.num1 === '' || telNumState.num2 === '' || telNumState.num3 === '')
                      }
                    />
                  </div>
                </div>
                {isCompanyUser && (
                  <>
                    <div className='inputForm-row'>
                      <div className='inputForm-col col-w100'>
                        <label htmlFor='emerNum'>{t('비상 연락처')}</label>
                        <NumbersInput
                          state={emerNumState}
                          setState={setEmerNumState}
                          lengthState={telNumLengthState}
                          setIsLastInputCompleted={setEmerNumLastInput}
                          isFirstInputFocused={telNumLastInput}
                          isSaveClicked={
                            isSaveClicked &&
                            !(emerNumState.num1 === '' && emerNumState.num2 === '' && emerNumState.num3 === '') &&
                            (emerNumState.num1 === '' || emerNumState.num2 === '' || emerNumState.num3 === '')
                          }
                        />
                      </div>
                    </div>
                    <div className='inputForm-row withBtn'>
                      <div className='inputForm-col col-w100'>
                        <label htmlFor='uBdate'>{t('생년월일')}</label>
                        <div className='flex-start iscontents-m'>
                          <div className='datePickerWrapper'>
                            <DatePickerComponent startDate={startDate} setDate={setStartDate} popperPlacement='bottom' isSaveClicked={isSaveClicked} />
                          </div>
                          <div className='flex-start radio-wrapper'>
                            <div className='flex-basic'>
                              <ClickableContainer onClick={() => handleClickSolarDateYn('0')} button={<TableLamp use={isSolarDate} />} />
                              <label htmlFor='양력'>{t('양력')}</label>
                            </div>
                            <div className='flex-basic'>
                              <ClickableContainer onClick={() => handleClickSolarDateYn('1')} button={<TableLamp use={!isSolarDate} />} />
                              <label htmlFor='음력'>{t('음력')}</label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {(isHeadUser || isCompanyUser) && (
                  <div className='inputForm-row'>
                    <div className='inputForm-col col-w50 expand-li'>
                      <label htmlFor='loginDate' className='required'>
                        {t('부서명')}
                      </label>
                      <SelectBox options={partList} defaultOption={data.partName || t('미선택')} state={selectedPartCd} setState={setSelectedPartCd} stateKey='PART_CD' filterbar='filter-1-left' />
                    </div>
                    <div className='inputForm-col col-w50 expand-li'>
                      <label htmlFor='loginDate' className='required'>
                        {t('직급')}
                      </label>
                      <SelectBox
                        options={positionList}
                        defaultOption={data.positionName || t('미선택')}
                        state={selectedPositionCd}
                        setState={setSelectedPositionCd}
                        stateKey='POSITION_CD'
                        filterbar='filter-1-left'
                      />
                    </div>
                  </div>
                )}
                {isCompanyUser && (
                  <>
                    <div className='inputForm-row'>
                      <div className='inputForm-col col-w50 labelOutInput'>
                        <label htmlFor='uAnnualnum'>{t('연차일수')}</label>
                        <Input type='text' id='uAnnualnum' name='uAnnualnum' state={signupInfo} setState={setSignupInfo} disabled />
                      </div>
                      <div className='inputForm-col col-w50 labelOutInput'>
                        <label htmlFor='uRannualnum'>{t('사용 연차일수')}</label>
                        <Input type='text' id='uRannualnum' name='uRannualnum' state={signupInfo} setState={setSignupInfo} disabled />
                      </div>
                    </div>
                    <div className='inputForm-row withBtn'>
                      <div className='inputForm-col col-w50'>
                        <label htmlFor={t('주소')}>{t('우편번호')}</label>
                        <div className='flex-start iscontents-m'>
                          <Input type='text' id='postNum' name='postNum' state={signupInfo} setState={setSignupInfo} disabled />
                          <AddressSearch className='btn-small' setState={setSignupInfo} />
                        </div>
                      </div>
                    </div>
                    <div className='inputForm-row'>
                      <div className='inputForm-col col-w50 labelOutInput'>
                        <label htmlFor={t('주소')}>{t('주소')}</label>
                        <Input type='text' id='address1' name='address1' state={signupInfo} setState={setSignupInfo} disabled />
                      </div>
                      <div className='inputForm-col col-w50 labelOutInput'>
                        <label htmlFor={t('상세주소')}>{t('상세주소')}</label>
                        <Input type='text' id='address2' name='address2' state={signupInfo} setState={setSignupInfo} placeholder={t('상세주소')} maxLength={25} />
                      </div>
                    </div>
                  </>
                )}
                <div className='inputForm-row'>
                  <div className='inputForm-col col-w100'>
                    <label htmlFor='hpNum'>{t('접속 IP')}</label>
                    <div className='flex-between'>
                      <IpInput
                        stateName='ipInfo'
                        separator='.'
                        state={ipInfoState}
                        setState={setIpInfoState}
                        setInfoState={setSignupInfo}
                        isFirstInputFocused={!isCompanyUser ? telNumLastInput : undefined}
                        isSaveClicked={
                          isSaveClicked &&
                          !(ipInfoState.num1 === '' && ipInfoState.num2 === '' && ipInfoState.num3 === '' && ipInfoState.num4 === '') &&
                          (ipInfoState.num1 === '' || ipInfoState.num2 === '' || ipInfoState.num3 === '' || ipInfoState.num4 === '')
                        }
                      />
                      <BtnGray className='btn-small' onClick={onClickGetCurrentIp}>
                        {t('현재 PC IP 적용')}
                      </BtnGray>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className='modal-footer flex-between'>
            <div className='flex-basic'>
              <BtnGhost onClick={onClickLogout}>
                <span className='material-symbols-rounded'>logout </span>
                {t('로그아웃')}
              </BtnGhost>
              <ModalHeaderActions>
                <button className='icon-btn' onClick={darkModeHandling} type='button' title={theme === 'light' ? t('다크모드 설정') : t('라이트모드 설정')}>
                  <span className='material-symbols-rounded'>{theme === 'light' ? 'dark_mode' : 'light_mode'}</span>
                </button>
                <div className='language-select-wrapper'>
                  <Language dropdownPosition='top' />
                </div>
              </ModalHeaderActions>
            </div>
            {userInfo.hCd.substring(0, 1).toLowerCase() !== 'm' && (
              <div className='flex-end'>
                <BtnBlue onClick={onClickSave}>{t('저장')}</BtnBlue>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </ModalBackground>
  );
};

export default UserInfoSettingModal;
